import React, { useEffect, useState } from 'react';
import { displayNombre, formatCOP, imageUrl } from '../services/api';
import CachedProductImage from './CachedProductImage';
import {
  allOpcionesAgotadas,
  firstDisponibleOpcion,
  needsOpcionPick,
  realOpciones,
} from '../utils/productoOpciones';

function isSinStock(product) {
  if (!product) return true;
  if (product.disponibilidad === 'sin_stock') return true;
  if (product.en_stock != null && Number(product.en_stock) === 0) return true;
  if (product.stock != null && Number(product.stock) === 0) return true;
  return product.cantidad != null && Number(product.cantidad) <= 0;
}

function displayCodigo(codigo) {
  const c = String(codigo || '').trim();
  if (!c || /^0+$/.test(c)) return '';
  return c.replace(/^0+(?=\d)/, '') || c;
}

/**
 * La foto se monta de una (sin esperar scroll).
 * priority = primeras del grid → carga más urgente.
 * Si hay sabores/opciones, el + abre el detalle para elegir.
 */
export default function ProductCard({ product, onPreview, onAdd, priority = false }) {
  const ops = realOpciones(product);
  const pickOpcion = needsOpcionPick(product);
  const [opcionId, setOpcionId] = useState(() => firstDisponibleOpcion(product)?.id || null);

  useEffect(() => {
    setOpcionId(firstDisponibleOpcion(product)?.id || null);
  }, [product?.id, product?.codigo]);

  const selectedOp = ops.find((o) => o.id === opcionId) || firstDisponibleOpcion(product);
  const img = imageUrl(selectedOp?.imagen || product.imagen);
  const [broken, setBroken] = useState(false);
  const name = displayNombre(product) || `Cód. ${product.codigo}`;
  const codigo = displayCodigo(product.codigo);
  const sinStock = isSinStock(product) || allOpcionesAgotadas(product);
  const showImg = Boolean(img) && !broken;

  return (
    <article className={`shop-card${sinStock ? ' shop-card--oos' : ''}`}>
      <div className="shop-card-media">
        <button type="button" className="shop-card-img" onClick={() => onPreview(product)}>
          {showImg ? (
            <CachedProductImage
              key={img}
              src={img}
              alt={name}
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={priority ? 'high' : 'auto'}
              sizes="(max-width: 640px) 50vw, (max-width: 1100px) 25vw, 20vw"
              onError={() => setBroken(true)}
              onLoad={() => setBroken(false)}
            />
          ) : (
            <div className="shop-card-placeholder">{img ? '' : 'Sin foto'}</div>
          )}
        </button>
        {sinStock ? <span className="shop-card-badge is-oos">Agotado</span> : null}
        {!sinStock ? (
          <button
            type="button"
            className="shop-card-plus"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (pickOpcion) {
                if (selectedOp?.disponible) {
                  onAdd(product, {
                    cantidad: 1,
                    opcion_id: selectedOp.id,
                    opcion_nombre: selectedOp.nombre,
                    imagen: selectedOp.imagen || product.imagen,
                  });
                  return;
                }
                onPreview(product);
                return;
              }
              onAdd(product);
            }}
            aria-label={`Añadir ${name} al carrito`}
          >
            +
          </button>
        ) : null}
        <div className="shop-card-price-bar">{formatCOP(product.precio)}</div>
      </div>
      <div className="shop-card-body">
        <button type="button" className="shop-card-name" onClick={() => onPreview(product)}>
          {name}
        </button>
        {codigo ? <span className="shop-card-code">{codigo}</span> : null}
        {/* Cualquier producto con opciones en admin (no solo exfoliantes). */}
        {pickOpcion ? (
          <div className="shop-card-ops" aria-label="Opciones">
            {ops.map((op) => {
              const src = imageUrl(op.imagen);
              const on = selectedOp?.id === op.id;
              return (
                <button
                  key={op.id}
                  type="button"
                  className={`shop-card-op${on ? ' is-on' : ''}${op.disponible ? '' : ' is-agotada'}`}
                  disabled={!op.disponible}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!op.disponible) return;
                    setOpcionId(op.id);
                  }}
                >
                  {src ? <CachedProductImage src={src} alt="" loading="lazy" /> : null}
                  <em>{op.nombre || 'Opción'}</em>
                </button>
              );
            })}
          </div>
        ) : null}
        {sinStock ? <span className="shop-card-stock is-oos">Agotado</span> : null}
      </div>
    </article>
  );
}
